import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Phone, Mail, Calendar, FileText, ShieldCheck } from "lucide-react";
import { useTheme } from "@/components/ThemeSwitcher";
import { LOGO_URL, LOGO_BG } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "adk-admin-token";

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

const STATUS_COLORS = {
  new: "var(--accent-primary)",
  contacted: "#3b82f6",
  completed: "#22c55e",
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bg-background)", color: "var(--text-primary)" }}>
      <form onSubmit={submit} data-testid="admin-login-form"
        className="w-full max-w-sm p-8 space-y-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: "var(--radius-btn)" }}>
        <div className="flex flex-col items-center gap-3 mb-2">
          <img src={LOGO_URL} alt="ADK Notary" className="h-16 w-16 rounded-full object-cover scale-[1.38]" style={{ background: LOGO_BG, border: "2px solid var(--accent-primary)" }} />
          <h1 className="font-heading text-2xl font-bold">Admin Login</h1>
        </div>
        <input data-testid="login-email" type="email" className="field px-4 py-3 w-full" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="login-password" type="password" className="field px-4 py-3 w-full" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="login-submit-btn" type="submit" disabled={loading} className="btn-accent w-full py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/appointments`, authHeader());
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/appointments/${id}/status?status=${status}`, {}, authHeader());
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch { toast.error("Could not update status"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-background)", color: "var(--text-primary)" }}>
      <header className="px-6 md:px-10 h-16 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border-color)", background: "var(--bg-surface)" }}>
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="ADK Notary" className="h-9 w-9 rounded-full object-cover scale-[1.38]" style={{ background: LOGO_BG }} />
          <span className="font-heading font-bold flex items-center gap-2"><ShieldCheck size={16} style={{ color: "var(--accent-primary)" }} /> ADK Notary Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="refresh-btn" onClick={load} className="btn-outline px-3 py-2 flex items-center gap-2 text-sm"><RefreshCw size={15} /> Refresh</button>
          <button data-testid="logout-btn" onClick={onLogout} className="btn-outline px-3 py-2 flex items-center gap-2 text-sm"><LogOut size={15} /> Logout</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Appointment Requests</h1>
          <span className="text-sm px-3 py-1 rounded-full" style={{ background: "var(--bg-surface)", color: "var(--text-secondary)" }} data-testid="appointment-count">{items.length} total</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={28} /></div>
        ) : items.length === 0 ? (
          <p className="text-center py-20" style={{ color: "var(--text-secondary)" }} data-testid="empty-state">No appointment requests yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="appointments-list">
            {items.map((a) => (
              <div key={a.id} data-testid={`appointment-${a.id}`} className="p-6" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: "var(--radius-btn)" }}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading text-lg font-semibold">{a.name}</h3>
                    <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{new Date(a.created_at).toLocaleString()}</p>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full font-medium capitalize" style={{ background: STATUS_COLORS[a.status] || "var(--accent-primary)", color: "#fff" }}>{a.status}</span>
                </div>
                <div className="space-y-1.5 text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
                  <p className="flex items-center gap-2"><Phone size={14} /> <a href={`tel:${a.phone}`} className="hover:opacity-70">{a.phone}</a></p>
                  <p className="flex items-center gap-2"><Mail size={14} /> <a href={`mailto:${a.email}`} className="hover:opacity-70">{a.email}</a></p>
                  <p className="flex items-center gap-2"><Calendar size={14} /> {a.preferred_date}</p>
                  <p className="flex items-center gap-2"><FileText size={14} /> {a.service_type}</p>
                  {a.documents && <p className="pl-6"><b>Docs:</b> {a.documents}</p>}
                  {a.message && <p className="pl-6"><b>Note:</b> {a.message}</p>}
                </div>
                <div className="flex gap-2">
                  {["new", "contacted", "completed"].map((s) => (
                    <button key={s} data-testid={`status-${s}-${a.id}`} onClick={() => setStatus(a.id, s)}
                      className="text-xs px-3 py-1.5 rounded-full capitalize transition-all"
                      style={{ border: "1px solid var(--border-color)", background: a.status === s ? "var(--accent-primary)" : "transparent", color: a.status === s ? "var(--accent-contrast)" : "var(--text-secondary)" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  useTheme();
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };
  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
